import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    Paper,
    InputAdornment,
    IconButton,
    CircularProgress,
    useTheme
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { login } from '../../Redux/Reducers/AuthReducer';
import { requestForToken } from '../../utils/firebase';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Menu, MenuItem } from '@mui/material';
import enFlag from '../../assets/images/flag/GB.png';
import frFlag from '../../assets/images/flag/FR.png';

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const { i18n, t } = useTranslation();
    const { loading } = useSelector((state) => state.AuthReducer);

    const [anchorEl, setAnchorEl] = useState(null);
    const openLangMenu = Boolean(anchorEl);

    const handleLangClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleLangClose = (lng) => {
        if (lng) i18n.changeLanguage(lng);
        setAnchorEl(null);
    };

    const [showPassword, setShowPassword] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            email: '',
            password: '',
        },
    });

    const onSubmit = async (data, e) => {
        e?.preventDefault();

        try {
            const fcmToken = await requestForToken();
            const payload = {
                ...data,
                fcmToken: fcmToken
            };

            const res = await dispatch(login(payload)).unwrap();
            toast.success(res.message);
            navigate('/dashboard');
        } catch (err) {
            console.log('LOGIN ERROR:', err);

            toast.error(
                typeof err === 'string'
                    ? err
                    : t('login_failed_msg', 'Login failed')
            );
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
                backgroundImage: 'url(/auth/auth-bg.webp)',
                backgroundSize: 'contain',
                backgroundPosition: 'top right',
                backgroundRepeat: 'no-repeat',
            }}
        >
            <Box sx={{ position: 'absolute', top: '20px', right: '20px' }}>
                <Button
                    type='button'
                    onClick={handleLangClick}
                    variant="outlined"
                    size="small"
                    startIcon={
                        <img
                            src={i18n.language.startsWith('fr') ? frFlag : enFlag}
                            alt="flag"
                            style={{ width: '20px', borderRadius: '2px' }}
                        />
                    }
                    sx={{
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                        color: '#fff',
                        textTransform: 'none',
                        backgroundColor: 'rgba(230, 210, 249, 0.2) !important',
                        borderRadius: '4px',
                        fontSize: '15px',
                        lineHeight: '24px',
                        minWidth: '80px'
                    }}
                >
                    {i18n.language.toUpperCase().substring(0, 2)}
                </Button>

                <Menu
                    anchorEl={anchorEl}
                    open={openLangMenu}
                    onClose={() => handleLangClose()}
                    slotProps={{
                        paper: {
                            sx: {
                                border: '1px solid rgba(230, 210, 249, 0.1)',
                                backgroundColor: 'rgba(18, 10, 35, 0.95)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: '4px',
                                mt: '10px',
                                color: '#fff',
                                padding: '0 !important',
                                fontSize: '15px',
                                lineHeight: '24px'
                            },
                        },
                    }}
                    sx={{
                        '& .MuiMenu-list': {
                            padding: 0,
                        },
                        '& .MuiMenuItem-root': {
                            gap: '10px',
                            py: '8px',
                            px: '16px',
                            '&:hover': {
                                backgroundColor: 'rgba(230, 210, 249, 0.1)',
                            }
                        }
                    }}
                >
                    <MenuItem onClick={() => handleLangClose('en')}>
                        <img src={enFlag} alt="EN" style={{ width: '20px', borderRadius: '2px' }} />
                        English
                    </MenuItem>
                    <MenuItem onClick={() => handleLangClose('fr')}>
                        <img src={frFlag} alt="FR" style={{ width: '20px', borderRadius: '2px' }} />
                        Français
                    </MenuItem>
                </Menu>
            </Box>
            <Container maxWidth={false} sx={{ maxWidth: '550px', }}>
                <Paper
                    elevation={24}
                    sx={{
                        background: 'rgba(255, 255, 255, 0)',
                        color: '#fff',
                        boxShadow: 'none',
                        borderRadius: 0,
                    }}
                >
                    <Box sx={{ mb: '20px' }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ color: 'white', mb: '10px', textAlign: 'center' }}>
                            {t('welcome_back', 'Welcome back to')}{' '}
                            <Box
                                component="span"
                                sx={{
                                    color: '#FF69B4',
                                }}
                            >
                                Solfana!
                            </Box>
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 1)', textAlign: 'center' }}>
                            {t('login_subtitle', 'Enter your login details to continue')}
                        </Typography>
                    </Box>

                    <form onSubmit={(e) => {
                            e.preventDefault(); // FORCE STOP
                            handleSubmit(onSubmit)(e);
                        }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            type="email"
                            {...register('email', {
                                required: t('email_required', 'Email is required'),
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: t('invalid_email', 'Invalid email address')
                                }
                            })}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            placeholder={t('enter_email', 'Enter your email')}
                            sx={{
                                mb: '20px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '999px',
                                    backgroundColor: 'transparent',
                                    color: '#fff',

                                    '& fieldset': {
                                        borderColor: 'rgba(230, 210, 249, 0.2)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(230, 210, 249, 0.2)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgba(230, 210, 249, 0.2)',
                                        borderWidth: '1px',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '12px 20px',
                                    color: '#FF69B4',
                                    '&:-webkit-autofill': {
                                        WebkitBoxShadow: '0 0 0 100px transparent inset',
                                        WebkitTextFillColor: '#FF69B4',
                                        transition: 'background-color 9999s ease-in-out 0s',
                                    },
                                    '&:-webkit-autofill:hover': {
                                        WebkitBoxShadow: '0 0 0 100px transparent inset',
                                    },
                                    '&:-webkit-autofill:focus': {
                                        WebkitBoxShadow: '0 0 0 100px transparent inset',
                                    },
                                },
                                '& input::placeholder': {
                                    color: 'rgba(255,255,255,0.6)',
                                    opacity: 1,
                                },
                                '& .MuiFormHelperText-root': {
                                    ml: '20px'
                                }
                            }}
                            InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                        />

                        <TextField
                            fullWidth
                            variant="outlined"
                            type={showPassword ? 'text' : 'password'}
                            {...register('password', {
                                required: t('password_required', 'Password is required')
                            })}
                            error={!!errors.password}
                            helperText={errors.password?.message}
                            placeholder={t('enter_password', 'Enter password')}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            sx={{ mr: '0px' }}
                                        >
                                            {!showPassword ? <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <path d="M9.46992 15.28C9.27992 15.28 9.08992 15.21 8.93992 15.06C8.11992 14.24 7.66992 13.15 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C13.1499 7.67004 14.2399 8.12004 15.0599 8.94004C15.1999 9.08004 15.2799 9.27004 15.2799 9.47004C15.2799 9.67004 15.1999 9.86004 15.0599 10L9.99992 15.06C9.84992 15.21 9.65992 15.28 9.46992 15.28ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 12.5 9.29992 12.98 9.53992 13.4L13.3999 9.54004C12.9799 9.30004 12.4999 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                                                <path d="M5.59984 18.51C5.42984 18.51 5.24984 18.45 5.10984 18.33C4.03984 17.42 3.07984 16.3 2.25984 15C1.19984 13.35 1.19984 10.66 2.25984 8.99998C4.69984 5.17998 8.24984 2.97998 11.9998 2.97998C14.1998 2.97998 16.3698 3.73998 18.2698 5.16998C18.5998 5.41998 18.6698 5.88998 18.4198 6.21998C18.1698 6.54998 17.6998 6.61998 17.3698 6.36998C15.7298 5.12998 13.8698 4.47998 11.9998 4.47998C8.76984 4.47998 5.67984 6.41998 3.51984 9.80998C2.76984 10.98 2.76984 13.02 3.51984 14.19C4.26984 15.36 5.12984 16.37 6.07984 17.19C6.38984 17.46 6.42984 17.93 6.15984 18.25C6.01984 18.42 5.80984 18.51 5.59984 18.51Z" fill="#FF69B4" />
                                                <path d="M12.0001 21.02C10.6701 21.02 9.37006 20.75 8.12006 20.22C7.74006 20.06 7.56006 19.62 7.72006 19.24C7.88006 18.86 8.32006 18.68 8.70006 18.84C9.76006 19.29 10.8701 19.52 11.9901 19.52C15.2201 19.52 18.3101 17.58 20.4701 14.19C21.2201 13.02 21.2201 10.98 20.4701 9.81C20.1601 9.32 19.8201 8.85 19.4601 8.41C19.2001 8.09 19.2501 7.62 19.5701 7.35C19.8901 7.09 20.3601 7.13 20.6301 7.46C21.0201 7.94 21.4001 8.46 21.7401 9C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02Z" fill="#FF69B4" />
                                                <path d="M12.6901 16.27C12.3401 16.27 12.0201 16.02 11.9501 15.66C11.8701 15.25 12.1401 14.86 12.5501 14.79C13.6501 14.59 14.5701 13.67 14.7701 12.57C14.8501 12.16 15.2401 11.9 15.6501 11.97C16.0601 12.05 16.3301 12.44 16.2501 12.85C15.9301 14.58 14.5501 15.95 12.8301 16.27C12.7801 16.26 12.7401 16.27 12.6901 16.27Z" fill="#FF69B4" />
                                                <path d="M1.99994 22.75C1.80994 22.75 1.61994 22.68 1.46994 22.53C1.17994 22.24 1.17994 21.76 1.46994 21.47L8.93994 14C9.22994 13.71 9.70994 13.71 9.99994 14C10.2899 14.29 10.2899 14.77 9.99994 15.06L2.52994 22.53C2.37994 22.68 2.18994 22.75 1.99994 22.75Z" fill="#FF69B4" />
                                                <path d="M14.5302 10.2199C14.3402 10.2199 14.1502 10.1499 14.0002 9.99994C13.7102 9.70994 13.7102 9.22994 14.0002 8.93994L21.4702 1.46994C21.7602 1.17994 22.2402 1.17994 22.5302 1.46994C22.8202 1.75994 22.8202 2.23994 22.5302 2.52994L15.0602 9.99994C14.9102 10.1499 14.7202 10.2199 14.5302 10.2199Z" fill="#FF69B4" />
                                            </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                <path d="M11.9999 16.33C9.60992 16.33 7.66992 14.39 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C14.3899 7.67004 16.3299 9.61004 16.3299 12C16.3299 14.39 14.3899 16.33 11.9999 16.33ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 13.56 10.4399 14.83 11.9999 14.83C13.5599 14.83 14.8299 13.56 14.8299 12C14.8299 10.44 13.5599 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                                                <path d="M12.0001 21.02C8.24008 21.02 4.69008 18.82 2.25008 15C1.19008 13.35 1.19008 10.66 2.25008 8.99998C4.70008 5.17998 8.25008 2.97998 12.0001 2.97998C15.7501 2.97998 19.3001 5.17998 21.7401 8.99998C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02ZM12.0001 4.47998C8.77008 4.47998 5.68008 6.41998 3.52008 9.80998C2.77008 10.98 2.77008 13.02 3.52008 14.19C5.68008 17.58 8.77008 19.52 12.0001 19.52C15.2301 19.52 18.3201 17.58 20.4801 14.19C21.2301 13.02 21.2301 10.98 20.4801 9.80998C18.3201 6.41998 15.2301 4.47998 12.0001 4.47998Z" fill="#FF69B4" />
                                            </svg>}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                mb: '20px',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '999px',
                                    backgroundColor: 'transparent',
                                    color: '#fff',

                                    '& fieldset': {
                                        borderColor: 'rgba(230, 210, 249, 0.2)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(230, 210, 249, 0.2)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgba(230, 210, 249, 0.2)',
                                        borderWidth: '1px',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    padding: '12px 20px',
                                    color: '#FF69B4',
                                    '&:-webkit-autofill': {
                                        WebkitBoxShadow: '0 0 0 100px transparent inset',
                                        WebkitTextFillColor: '#FF69B4',
                                        transition: 'background-color 9999s ease-in-out 0s',
                                    },
                                    '&:-webkit-autofill:hover': {
                                        WebkitBoxShadow: '0 0 0 100px transparent inset',
                                    },
                                    '&:-webkit-autofill:focus': {
                                        WebkitBoxShadow: '0 0 0 100px transparent inset',
                                    },
                                },
                                '& input::placeholder': {
                                    color: 'rgba(255,255,255,0.6)',
                                    opacity: 1,
                                },
                                '& .MuiFormHelperText-root': {
                                    ml: '20px'
                                }
                            }}
                            InputLabelProps={{ sx: { color: 'rgba(255, 255, 255, 0.7)' } }}
                        />

                        <Box sx={{ textAlign: 'right', mb: '30px' }}>
                            <Link to="/forgot-password" style={{ textDecoration: 'none', color: '#FF69B4', fontWeight: '600', fontSize: '16px' }}>
                                {t('forgot_password_link', 'Forgot Password?')}
                            </Link>
                        </Box>

                        <Button
                            fullWidth
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                borderRadius: '999px',
                                fontWeight: '400',
                                fontSize: '16px',
                                lineHeight: '24px',
                                textTransform: 'none',
                                background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : t('sign_in', 'Sign In')}
                        </Button>
                    </form>
                </Paper>
            </Container>
        </Box>
    );
};

export default Login;
